// Unified Cart Logic with Storefront API
// Last updated: 2026-04-20 (Cache-bust v1.3 - Stock validation & smart add/update)

import { GQL_QUERIES } from './cartUnifiedGQLFragments4.js';

class StorefrontCart {
  constructor(config) {
    this.domain = config.domain;
    this.storefrontToken = config.storefrontToken;
    this.apiVersion = config.apiVersion || '2026-04';
    this.apiUrl = `https://${this.domain}/api/${this.apiVersion}/graphql.json`;
    
    this.cookieName = 'storefront_cart_id';
    this.cartId = null;
    this.cartData = null;

    this.init();
  }

  async init() {
    console.log('[StorefrontCart] Inicializando carrito...');
    // this.hydrateFromUrl();

    this.cartId = this.getCookie(this.cookieName);

    if (this.cartId) {
      await this.fetchCart();
    } else {
      await this.createCart();
    }

    // this.setupCrossDomainLinks();
  }

  // MÉTODOS DE API

  async fetchGraphQL(query, variables = {}) {
    try {
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.storefrontToken,
        },
        body: JSON.stringify({ query, variables }),
      });
      const json = await response.json();
      if (json.errors) throw new Error(json.errors[0].message);
      return json.data;
    } catch (error) {
      console.error('[StorefrontCart] API Error:', error);
      return null;
    }
  }

  async fetchCart() {
    const data = await this.fetchGraphQL(GQL_QUERIES.getCart, { cartId: this.cartId });
    console.log('[StorefrontCart] Carrito obtenido:', data);
    // Si el carrito expiró o es null, creamos uno nuevo
    if (!data || !data.cart) {
      console.warn('[StorefrontCart] Carrito no válido o expirado. Creando nuevo...');
      await this.createCart();
    } else {
      this.cartData = data.cart;
      this.dispatchUpdateEvent();
    }
  }

  async createCart(lines = []) {
    const data = await this.fetchGraphQL(GQL_QUERIES.createCart, { input: { lines } });
    if (data && data.cartCreate.cart) {
      this.cartData = data.cartCreate.cart;
      this.setCartId(this.cartData.id);
      this.dispatchUpdateEvent();
    }
  }

  async addItems(lines) {
    if (!this.cartId) await this.createCart();
    const data = await this.fetchGraphQL(GQL_QUERIES.addLines, { cartId: this.cartId, lines });
    if (data && data.cartLinesAdd.cart) {
      this.cartData = data.cartLinesAdd.cart;
      this.dispatchUpdateEvent();
    }
  }

  // UTILIDADES DE PERSISTENCIA Y DOMINIOS

  setCartId(id) {
    this.cartId = id;
    const rootDomain = window.location.hostname.split('.').slice(-2).join('.');
    const expires = new Date();
    expires.setDate(expires.getDate() + 10); // Expiración de 10 días
    document.cookie = `${this.cookieName}=${encodeURIComponent(id)}; expires=${expires.toUTCString()}; path=/; domain=.${rootDomain}; SameSite=Lax; Secure`;
  }

  getCookie(name) {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
  }

  hydrateFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const urlCartId = params.get('cart_id');
    
    if (urlCartId) {
      this.setCartId(urlCartId);
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('cart_id');
      window.history.replaceState({}, document.title, newUrl.toString());
    }
  }

  setupCrossDomainLinks() {
    document.body.addEventListener('click', (e) => {
      console.log('DETAIL:',e)
      const link = e.target.closest('a');
      const isPagination = link.classList.contains('c-pagination__item')
      if (isPagination || !link || !link.href) return;

      try {
        const url = new URL(link.href, window.location.origin);
        
        if (url.pathname === '/cart') {
          const cartUrl = new URL(`https://${this.domain}/cart`);
          if (this.cartId) {
            cartUrl.searchParams.set('cart_id', this.cartId);
          }
          link.href = cartUrl.toString();
          return;
        }

        const currentRoot = window.location.hostname.split('.').slice(-2).join('.');
        const targetRoot = url.hostname.split('.').slice(-2).join('.');

        if (currentRoot !== targetRoot && this.cartId) {
          url.searchParams.set('cart_id', this.cartId);
          link.href = url.toString();
        }
      } catch (error) {
        // Ignorar enlaces inválidos
      }
    });
  }

  async updateLines(lines) {
    const data = await this.fetchGraphQL(GQL_QUERIES.updateLines, { cartId: this.cartId, lines });
    console.log('Update data:', data)
    if (data && data.cartLinesUpdate.cart) {
      this.cartData = data.cartLinesUpdate.cart;
      this.dispatchUpdateEvent();
    }
  }

  // VALIDACIÓN DE STOCK Y ACTUALIZACIÓN INTELIGENTE

  findExistingLine(merchandiseId, attributes = []) {
    if (!this.cartData || !this.cartData.lines) return null;

    const items = this.cartData.lines.edges.map(edge => edge.node);
    
    const criticalKeys = ['productType', 'pickup', 'pickupStoreId'];
    
    return items.find(item => {
      if (item.merchandise.id !== merchandiseId) return false;

      const itemAttrs = item.attributes || [];
      
      const itemIsFree = itemAttrs.some(attr => attr.key === 'productFree');
      const newIsFree = attributes.some(attr => attr.key === 'productFree');
      
      if (itemIsFree !== newIsFree) return false;
      
      if (itemIsFree && newIsFree) {
        const itemParent = itemAttrs.find(attr => attr.key === 'productFree')?.value;
        const newParent = attributes.find(attr => attr.key === 'productFree')?.value;
        if (itemParent !== newParent) return false;
      }

      for (const criticalKey of criticalKeys) {
        const itemValue = itemAttrs.find(attr => attr.key === criticalKey)?.value || '';
        const newValue = attributes.find(attr => attr.key === criticalKey)?.value || '';
        if (itemValue !== newValue) return false;
      }

      return true;
    });
  }

  async fetchVariantStock(variantId, productUrl = null) {
    try {
      const numericId = typeof variantId === 'string' && variantId.includes('gid://')
        ? parseInt(variantId.split('/').pop())
        : parseInt(variantId);

      let handle = null;
      
      if (productUrl) {
        const match = productUrl.match(/\/products\/([^\/\?]+)/);
        if (match) handle = match[1];
      }
      
      if (!handle) {
        const productUrlAttr = document.querySelector('[data-product-url]');
        if (productUrlAttr) {
          const url = productUrlAttr.getAttribute('data-product-url');
          const match = url.match(/\/products\/([^\/\?]+)/);
          if (match) handle = match[1];
        }
      }

      if (handle) {
        const response = await fetch(`/products/${handle}.js`);
        if (response.ok) {
          const productData = await response.json();
          const variant = productData.variants.find(v => v.id === numericId);
          if (variant && typeof variant.inventory_quantity === 'number') {
            return variant.inventory_quantity;
          }
        }
      }
      
      console.warn(`[StorefrontCart] No se pudo obtener stock para variant ${numericId}, permitiendo agregar`);
      return 999;
    } catch (error) {
      console.error('[StorefrontCart] Error al obtener stock:', error);
      return 999; // Valor por defecto alto en caso de error
    }
  }

  async addOrUpdateItems(lines) {
    if (!this.cartId) await this.createCart();

    const linesToAdd = [];
    const linesToUpdate = [];
    
    // Procesar cada línea
    for (const line of lines) {
      const { merchandiseId, quantity, attributes = [], productUrl = null } = line;
      
      const existingLine = this.findExistingLine(merchandiseId, attributes);

      if (existingLine) {
        const availableQtyToAdd = this.verifyAvailability(existingLine, quantity)
        this.addedQuantity = availableQtyToAdd
        console.log('AVAILABLE',availableQtyToAdd)
        if(availableQtyToAdd <= 0) {
          throw new Error('Cantidad máxima alcanzada para este artículo en el carrito.');
        } else if(availableQtyToAdd < quantity) {
          this.quantityWarningNotification(`Debido a la disponibilidad, solo se añadieron ${existingLine.quantity + availableQtyToAdd} artículos al carrito.`)
        }

        this.quantityWarningNotification('', 'off');
        if(availableQtyToAdd > 0) {
          linesToUpdate.push({
            id: existingLine.id,
            quantity: existingLine.quantity + availableQtyToAdd
          });
        }
      } else {
        this.quantityWarningNotification('', 'off');
        const { productUrl: _, ...lineWithoutUrl } = line;
        linesToAdd.push(lineWithoutUrl);
      }
    }

    if (linesToUpdate.length > 0) {
      await this.updateLines(linesToUpdate);
    }
    
    if (linesToAdd.length > 0) {
      const data = await this.fetchGraphQL(GQL_QUERIES.addLines, { 
        cartId: this.cartId, 
        lines: linesToAdd 
      });
      console.log('Data:', data)
      const addedLine = linesToAdd[0]
      const recentlyAdded = data.cartLinesAdd.cart.lines.edges.filter(edge => edge.node.merchandise.id === addedLine.merchandiseId)
      console.log(recentlyAdded)
      const availableRecentlyAddedItems = recentlyAdded[0].node.merchandise.quantityAvailable
      this.addedQuantity = recentlyAdded[0].node.quantity
      if(availableRecentlyAddedItems < addedLine.quantity) {
        this.quantityWarningNotification(`Debido a la disponibilidad, solo se añadieron ${this.addedQuantity} artículos al carrito.`)
      }

      if (data && data.cartLinesAdd.cart) {
        this.cartData = data.cartLinesAdd.cart;
        this.dispatchUpdateEvent();
      }
    }
    
    if (linesToUpdate.length > 0 && linesToAdd.length > 0) {
      await this.fetchCart();
    }

    return {
      addedQuantity: this.addedQuantity
    }
  }

  quantityWarningNotification (message, toggle = 'on') {
    const errorContainer = document.querySelector('.js-product-message')
    const errorMessage = errorContainer.querySelector('[text-message-warning]')

    if(toggle === 'off') {
      errorContainer.classList.add('d-none')
      errorMessage.innerHTML = ''
      return
    }
    errorContainer.classList.remove('d-none')
    errorMessage.innerHTML = message
    return
  }

  verifyAvailability (existingLine, attemptedAddedQty) {
    const lineAvailability = existingLine.merchandise.quantityAvailable
    const currentQtyInCart = existingLine.quantity
    const willBeToCart = currentQtyInCart + attemptedAddedQty
    if (willBeToCart > lineAvailability) {
      return lineAvailability - currentQtyInCart
    }

    return attemptedAddedQty
  }

  dispatchUpdateEvent() {
    const event = new CustomEvent('storefront:cart:updated', {
      detail: { cart: this.cartData }
    });
    document.dispatchEvent(event);
  }
}

// Inicialización global
window.CartAPI = new StorefrontCart({
  domain: 'allnutrition.cl', // El dominio principal configurado en Shopify
  storefrontToken: '59067afe93dc96dcc4f5cd31f5fef2bb'
});